import { IAppContext } from 'App/AppContext';
import { mapFromControlDashboard } from 'mappings/controlMappings';
import { mapFromDashboard } from 'mappings/dashboardMapping';
import { mapFromTheme, mapFromThemeContext, mapFromThemes, mapFromThemeScene, mapToTheme } from 'mappings/themeMapping';
import { ControlDashboard } from 'models/control';
import Dashboard from 'models/dashboard';
import { ControlDashboardDTO } from 'models/dto/controlDTO';
import ControlISOControlDTO from 'models/dto/controlISOControlDTO';
import { ControlThemeInheritInfoDTO } from 'models/dto/controlThemeInheritInfoDTO';
import DashboardDTO from 'models/dto/dashboardDTO';
import EntityRelationDTO from 'models/dto/entityRelationDTO';
import NormThemeDTO from 'models/dto/normThemeDTO';
import { ObjectiveThemeDTO } from 'models/dto/Objective/objectiveThemeDTO';
import { ProcessThemeDTO } from 'models/dto/Process/processThemeDTO';
import ThemeContextDTO from 'models/dto/themeContextDTO';
import ThemeDTO from 'models/dto/themeDTO';
import ThemeSceneDTO from 'models/dto/themeSceneDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import Theme from 'models/theme';
import ThemeContext from 'models/themeContext';
import ThemeScene from 'models/themeScene';
import AppError from 'utils/appError';
import { toApiDateTimeOptional } from 'utils/datetime';
import http from './httpService';

export async function apiGetThemes(accessToken: string, globalDataCache: GlobalDataCache): Promise<Theme[]> {
  try {
    const ar = await http.get<ThemeDTO[]>(`/themes`, http.getRequestConfig(accessToken));

    return mapFromThemes(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetThemesForTask(
  taskId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Theme[]> {
  try {
    const res = await http.get<ThemeDTO[]>(`/themes/task/${taskId}`, http.getRequestConfig(accessToken));

    return mapFromThemes(res.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetThemeChildren(
  themeId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Theme[]> {
  try {
    const ar = await http.get<ThemeDTO[]>(`/themes/${themeId}/children`, http.getRequestConfig(accessToken));

    return mapFromThemes(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetTheme(
  themeId: number,
  includeISOControls: boolean,
  includePackageInstructions: boolean,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Theme | undefined> {
  try {
    if (!themeId || themeId < 0) return undefined;
    const ar = await http.get<ThemeDTO>(
      `/themes/${themeId}?includeISOControls=${includeISOControls}&includePackageInstructions=${includePackageInstructions}`,
      http.getRequestConfig(accessToken),
    );
    if (!ar.data) return undefined;

    return mapFromTheme(ar.data, globalDataCache) as Theme;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetThemeCode(accessToken: string, code: string): Promise<number> {
  try {
    const ar = await http.post<number>(`/themes/code`, { valueString: code }, http.getRequestConfig(accessToken));

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddTheme(theme: Theme, accessToken: string, globalDataCache: GlobalDataCache): Promise<Theme> {
  try {
    const themeDTO = mapToTheme(theme);
    const ar = await http.post<ThemeDTO>(`/themes`, themeDTO, http.getRequestConfig(accessToken));

    return mapFromTheme(ar.data, globalDataCache) as Theme;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateTheme(
  theme: Theme,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Theme> {
  try {
    const themeDTO: ThemeDTO = mapToTheme(theme) as ThemeDTO;
    const ar = await http.put<ThemeDTO>('/themes', themeDTO, http.getRequestConfig(accessToken));

    return mapFromTheme(ar.data, globalDataCache) as Theme;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveTheme(theme: Theme, accessToken: string): Promise<boolean> {
  try {
    await http.post(`/themes/delete/${theme.themeId}`, null, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetThemeScene(accessToken: string, globalDataCache: GlobalDataCache): Promise<ThemeScene> {
  try {
    const ar = await http.get<ThemeSceneDTO>(`/themes/scene`, http.getRequestConfig(accessToken));

    return mapFromThemeScene(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiSetCustomNorm(
  normId: number,
  themeId: number,
  add: boolean,
  accessToken: string,
): Promise<void> {
  try {
    const normThemeDTO: NormThemeDTO = new NormThemeDTO(normId, themeId);
    await http.post(`/themes/norms/?add=${add}`, normThemeDTO, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiSetThemeISOControl(
  isoControlId: number,
  themeId: number,
  add: boolean,
  accessToken: string,
): Promise<void> {
  try {
    const relation = new ControlISOControlDTO(isoControlId, themeId);
    await http.post(`/themes/iso/?add=${add}`, relation, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCreateSchema(auditYear: number, accessToken: string): Promise<void> {
  try {
    await http.post(`/themes/audit/createtasks?year=${auditYear}`, null, http.getRequestConfig(accessToken));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveThemeFromTheme(
  themeIdFrom: number,
  themeIdTo: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new EntityRelationDTO();
    body.idFrom = themeIdFrom;
    body.idTo = themeIdTo;
    await http.post<EntityRelationDTO>(`/themes/theme?add=${false}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddThemeToTheme(
  themeIdFrom: number,
  themeIdTo: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new EntityRelationDTO();
    body.idFrom = themeIdFrom;
    body.idTo = themeIdTo;
    await http.post<EntityRelationDTO>(`/themes/theme?add=${true}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetThemeContext(
  themeId: number,
  accessToken: string,
  appContext: IAppContext,
): Promise<ThemeContext> {
  try {
    const ar = await http.get<ThemeContextDTO>(`/themes/context/${themeId}`, http.getRequestConfig(accessToken));

    return mapFromThemeContext(ar.data, appContext);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Control
export async function apiRemoveControlFromTheme(
  controlIdFrom: number,
  themeId: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new EntityRelationDTO();
    body.idFrom = controlIdFrom;
    body.idTo = themeId;
    await http.post<EntityRelationDTO>(`/themes/control?add=${false}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddControlToTheme(
  controlIdFrom: number,
  themeId: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new EntityRelationDTO();
    body.idFrom = controlIdFrom;
    body.idTo = themeId;
    await http.post<EntityRelationDTO>(`/themes/control?add=${true}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Risk

export async function apiGetThemesForRisk(
  riskId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Theme[]> {
  try {
    const res = await http.get<ThemeDTO[]>(`/themes/risk/${riskId}`, http.getRequestConfig(accessToken));

    return mapFromThemes(res.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//Objective
export async function apiAddObjectiveToTheme(
  objectiveIdFrom: number,
  themeId: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new ObjectiveThemeDTO(themeId, objectiveIdFrom);
    await http.post<EntityRelationDTO>(`/objectives/theme?add=${true}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveObjectiveFromTheme(
  objectiveIdFrom: number,
  themeId: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new ObjectiveThemeDTO(themeId, objectiveIdFrom);
    await http.post<ObjectiveThemeDTO>(`/objectives/theme?add=${false}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//process
export async function apiAddProcessToTheme(
  processIdFrom: number,
  themeId: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new ProcessThemeDTO(processIdFrom, themeId);
    await http.post<ProcessThemeDTO>(`/processes/theme?add=${true}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveProcessFromTheme(
  processIdFrom: number,
  themeId: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new ProcessThemeDTO(processIdFrom, themeId);
    await http.post<ProcessThemeDTO>(`/processes/theme?add=${false}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//normId restricts the walk to the hierarchy of one framework instead of the whole requirement tree
export async function apiGetNextTheme(
  themeId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
  normId?: number,
): Promise<Theme | undefined> {
  try {
    const query = normId ? `?normId=${normId}` : '';
    const ar = await http.get<ThemeDTO>(`/themes/${themeId}/next${query}`, http.getRequestConfig(accessToken));

    return mapFromTheme(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//normId restricts the walk to the hierarchy of one framework instead of the whole requirement tree
export async function apiGetPrevTheme(
  themeId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
  normId?: number,
): Promise<Theme | undefined> {
  try {
    const query = normId ? `?normId=${normId}` : '';
    const ar = await http.get<ThemeDTO>(`/themes/${themeId}/prev${query}`, http.getRequestConfig(accessToken));

    return mapFromTheme(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetThemeInheritOptions(
  themeId: number,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Theme[]> {
  try {
    const ar = await http.get<ThemeDTO[]>(`/themes/${themeId}/inherit`, http.getRequestConfig(accessToken));

    return mapFromThemes(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateThemeInheritOptions(
  sourceThemeId: number,
  orgUnitId: string,
  themeId: number,
  copyImpl: boolean,
  overwriteImpl: boolean,
  copyStat: boolean,
  copyAppl: boolean,
  copyCust: boolean,
  copyLib: boolean,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Theme | undefined> {
  try {
    const dto = new ControlThemeInheritInfoDTO();
    dto.orgUnitId = orgUnitId;
    dto.id = themeId;
    dto.copyImplementation = copyImpl;
    dto.overwriteImplementation = overwriteImpl;
    dto.copyStatus = copyStat;
    dto.copyApplicability = copyAppl;
    dto.copyCustomStandards = copyCust;
    dto.copyLibraryItems = copyLib;

    const ar = await http.post<ThemeDTO>(`/themes/${sourceThemeId}/inherit`, dto, http.getRequestConfig(accessToken));

    return mapFromTheme(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

//dashboard
export async function apiCreateThemeDashboard(
  themeId: number,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Dashboard> {
  try {
    const ar = await http.post<DashboardDTO>(`/themes/${themeId}/dashboard`, null, http.getRequestConfig(accessToken));

    return mapFromDashboard(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetThemeDashboard(
  themeId: number,
  startDate: Date | undefined,
  endDate: Date | undefined,
  accessToken: string,
): Promise<ControlDashboard> {
  try {
    const ar = await http.get<ControlDashboardDTO>(`/themes/${themeId}/dashboard`, {
      ...http.getRequestConfig(accessToken),
      params: {
        startDate: toApiDateTimeOptional(startDate),
        endDate: toApiDateTimeOptional(endDate),
      },
    });

    return mapFromControlDashboard(ar.data);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetThemesOnly(accessToken: string, globalDataCache: GlobalDataCache): Promise<Theme[]> {
  try {
    const ar = await http.get<ThemeDTO[]>(`/themes?excludeIsoControls=true`, http.getRequestConfig(accessToken));

    return mapFromThemes(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
